import { payments } from "@fm/api";
import type {
  Payment,
  CreatePaymentPayload,
  PaymentListParams,
  ApiResponse,
} from "@fm/api";
import type { ApiClient } from "@fm/api/types";
import { getApiBaseUrl } from "../lib/config";

export interface UsePaymentsOptions {
  apiKey: string;
  fleetId: string;
  apiBaseUrl?: string;
}

export interface UsePaymentsReturn {
  createPayment: (
    payload: CreatePaymentPayload,
  ) => Promise<ApiResponse<Payment>>;
  confirmPayment: (paymentId: string) => Promise<ApiResponse<Payment>>;
  getPayments: (params?: PaymentListParams) => Promise<ApiResponse<Payment[]>>;
  getRentalPayments: (rentalId: string) => Promise<ApiResponse<Payment[]>>;
  getPayment: (paymentId: string) => Promise<ApiResponse<Payment>>;
  calculatePaymentStatus: (
    rentalId: string,
    totalAmount: number,
  ) => Promise<{
    isPaid: boolean;
    totalPaid: number;
    remainingAmount: number;
    payments: Payment[];
  }>;
}

export function usePayments(options: UsePaymentsOptions): UsePaymentsReturn {
  const { apiKey, fleetId, apiBaseUrl } = options;

  // Create API client with the provided apiKey
  const apiClient: ApiClient = {
    baseUrl: getApiBaseUrl(apiBaseUrl),
    apiKey,
  };

  const createPayment = async (payload: CreatePaymentPayload) => {
    return payments.create(apiClient, payload);
  };

  const confirmPayment = async (paymentId: string) => {
    return payments.confirm(apiClient, paymentId);
  };

  const getPayments = async (params?: PaymentListParams) => {
    return payments.list(apiClient, params);
  };

  const getRentalPayments = async (rentalId: string) => {
    return payments.list(apiClient, { rental_id: rentalId });
  };

  const getPayment = async (paymentId: string) => {
    return payments.get(apiClient, paymentId);
  };

  const calculatePaymentStatus = async (
    rentalId: string,
    totalAmount: number,
  ) => {
    const paymentsResponse = await getRentalPayments(rentalId);

    if (!paymentsResponse.data) {
      return {
        isPaid: false,
        totalPaid: 0,
        remainingAmount: totalAmount,
        payments: [],
      };
    }

    const successfulPayments = paymentsResponse.data.filter(
      (payment) => payment.status === "succeeded",
    );

    const totalPaid = successfulPayments.reduce(
      (sum, payment) => sum + parseFloat(payment.amount),
      0,
    );

    const remainingAmount = Math.max(0, totalAmount - totalPaid);
    const isPaid = remainingAmount === 0;

    return {
      isPaid,
      totalPaid,
      remainingAmount,
      payments: paymentsResponse.data,
    };
  };

  return {
    createPayment,
    confirmPayment,
    getPayments,
    getRentalPayments,
    getPayment,
    calculatePaymentStatus,
  };
}
